import { fullLabel } from "../components/WhereItIs";
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { prov, provTap } from "@/lib/provenance";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { scanAccept, scanRefuse, scanWarn } from "@/lib/scanFeedback";
import { SoundToggle } from "@/components/SoundToggle";
import { ScanField } from "@/components/ScanField";
import { useFloorReturn } from "@/lib/floorReturn";
import { useFloorActions } from "@/lib/floor";
import { MapPin, ScanLine, CheckCircle2, XCircle, Package, Boxes, Truck, AlertTriangle, Undo2, Layers } from "lucide-react";

const ZONES_ONLY_KEY = "moveItem.zonesOnly";

const KIND_META = {
  part: { icon: Package, word: "part" },
  container: { icon: Boxes, word: "container" },
  donor: { icon: Truck, word: "truck" },
};

export default function MoveItemPage() {
  const [locations, setLocations] = useState([]);
  const [dest, setDest] = useState(null);      // { code, id, label }
  const [log, setLog] = useState([]);
  const [last, setLast] = useState(null);
  // Floor techs reach this via the "Put Away" tile (Floor round-trip). Show the
  // owner-editable Floor label there; the desk keeps "Move Item" for the office.
  const fr = useFloorReturn();
  const homeActions = useFloorActions("home");
  const inFloor = !!fr?.active;
  const floorTitle = homeActions.find((a) => a.code === "home_putaway")?.label || "Put Away";
  // FAST YARD MODE — scope the destination to yard ZONES only (remembered across visits).
  const [zonesOnly, setZonesOnly] = useState(() => localStorage.getItem(ZONES_ONLY_KEY) === "1");

  useEffect(() => {
    api.get("/locations").then(({ data }) => setLocations(data.filter((l) => l.type !== "site"))).catch(() => {});
  }, []);

  const locById = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l])), [locations]);
  const locByCode = useMemo(() => Object.fromEntries(locations.map((l) => [String(l.short_code || "").toUpperCase(), l])), [locations]);
  const destOptions = useMemo(() => (zonesOnly ? locations.filter((l) => l.type === "zone") : locations), [locations, zonesOnly]);

  // Flip fast mode: persist, and drop any active destination so it must be re-picked in scope.
  const toggleZonesOnly = () => setZonesOnly((on) => {
    const next = !on;
    localStorage.setItem(ZONES_ONLY_KEY, next ? "1" : "0");
    setDest(null);
    return next;
  });

  // ---------- DESTINATION (sticky) — three inputs: scan / dropdown / type ----------
  const setDestByScan = async (code) => {
    if (!code) return;
    try {
      const { data } = await api.get("/locations/resolve", { params: { scan: code } });
      if (zonesOnly && data.type !== "zone") {
        scanRefuse(`${data.short_code} is not a yard zone — fast mode accepts zones only.`);
        toast.error(`${data.short_code} is not a yard zone — fast mode accepts zones only.`);
        return;
      }
      setDest({ code: data.short_code, id: data.id, label: `${data.short_code} · ${fullLabel(data)}` });
      scanAccept(`MOVING TO ${data.short_code}`);
      toast.success(`Destination → ${data.short_code}`);
    } catch (e) {
      scanRefuse(formatApiErrorDetail(e.response?.data?.detail) || "That scan isn't a known location.");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That scan isn't a known location.");
    }
  };
  const pickDest = (id) => { const l = locById[id]; if (l) setDest({ code: l.short_code, id: l.id, label: `${l.short_code} · ${fullLabel(l)}` }); };
  const typeDest = (code) => {
    const c = code.trim();
    if (!c) { setDest(null); return; }
    if (zonesOnly) {
      const l = locByCode[c.toUpperCase()];
      if (!l || l.type !== "zone") { scanRefuse(`"${c}" is not a yard zone — fast mode accepts zones only.`); toast.error(`"${c}" is not a yard zone — fast mode accepts zones only.`); return; }
      setDest({ code: l.short_code, id: l.id, label: `${l.short_code} · ${fullLabel(l)}` });
      return;
    }
    setDest({ code: c, id: null, label: c });
  };

  // ---------- FLASH + LOG (non-blocking Put Away rhythm) ----------
  const flash = (entry) => { setLast(entry); setLog((l) => [entry, ...l].slice(0, 100)); };
  const okFlash = (e) => {
    const what = e.part?.short_code || e.container?.short_code || e.code || "MOVED";
    if (e.warn) scanWarn(e.message || what); else scanAccept(what);
    flash({ ok: true, ts: Date.now(), ...e });
  };
  const failFlash = (code, message) => { scanRefuse(message || "THAT DIDN'T MOVE"); flash({ ok: false, code, message, ts: Date.now() }); };
  // Caught duplicate — part already in this destination (re-scan). Amber, distinct tone.
  const dupeFlash = (e) => {
    scanWarn(`${e.part?.short_code || e.container?.short_code || "IT"} IS ALREADY THERE`);
    flash({ ok: true, already: true, ts: Date.now(), ...e });
  };

  // ---------- MOVE — item-first, auto-detect kind, dispatch to the right endpoint ----------
  const moveItem = async (code, meta) => {
    if (!code) return;
    if (!dest) { toast.error("Set a destination first."); return; }
    try {
      const { data: r } = await api.get("/scan/resolve", { params: { code } });
      const itemLabel = r.label || code;
      if (r.kind === "part") {
        const { data } = await api.post("/putaway", { part_scan: code, location_id: dest.code,
                                                      provenance: prov("move_item", meta) });
        if (data.already) return dupeFlash({ code, kind: "part", label: data.part.short_code, sub: "already in this location — pull tag away",
                  line: `${data.part.short_code} · already in ${dest.code}` });
        if (!data.ok) return failFlash(code, data.message);
        okFlash({ code, kind: "part", label: data.part.short_code, sub: data.part.name, itemId: r.id, prevId: data.from_location_id ?? null,
                  line: `${data.part.short_code} → ${data.location.short_code}` });
      } else if (r.kind === "location") {
        const loc = locById[r.id];
        if (!loc || loc.type !== "container") return failFlash(code, "That's a fixed location, not a movable item — scan a part, tote, or truck.");
        const { data } = await api.post("/containers/move", { container_scan: code, location_scan: dest.code,
                                                              provenance: prov("move_item", meta) });
        if (!data.ok) return failFlash(code, data.message);
        okFlash({ code, kind: "container", label: data.container.short_code, warn: data.moved_parts === 0, itemId: r.id, prevId: data.from_parent_id ?? null,
                  sub: data.moved_parts === 0 ? "0 parts came with it — empty/wrong tote?" : `${data.moved_parts} part${data.moved_parts === 1 ? "" : "s"} came with it`,
                  line: `${data.container.short_code} → ${data.destination.short_code}` });
      } else if (r.kind === "donor") {
        const { data } = await api.post(`/donors/${r.id}/place`, { location_scan: dest.code, confirm: true,
                                                                   provenance: prov("move_item", meta) });
        if (!data.ok) return failFlash(code, data.message);
        okFlash({ code, kind: "donor", label: itemLabel, sub: "truck placed", itemId: r.id, prevId: data.from_site_location_id ?? null,
                  line: `${itemLabel} → ${data.destination?.short_code || dest.code}` });
      } else {
        failFlash(code, "That scan didn't resolve to a part, tote, or truck.");
      }
    } catch (e) {
      failFlash(code, formatApiErrorDetail(e.response?.data?.detail) || "Move failed.");
    }
  };

  const okCount = log.filter((x) => x.ok && !x.warn && !x.already).length;
  const dupCount = log.filter((x) => x.already).length;
  const errCount = log.filter((x) => !x.ok).length;

  const finishBatch = () => {
    scanAccept(`${okCount} MOVED`);
    const dup = dupCount > 0 ? ` · ${dupCount} re-read${dupCount === 1 ? "" : "s"} caught` : "";
    toast.success(`Batch done — ${okCount} item${okCount === 1 ? "" : "s"} moved${dest ? ` → ${dest.code}` : ""}${dup}.`);
    setLog([]); setLast(null);
  };

  const undoMove = async (entry) => {
    if (!entry?.itemId || entry.undone) return;
    try {
      const { data } = await api.post("/move-item/undo", { kind: entry.kind, item_id: entry.itemId, previous_location_id: entry.prevId ?? null, provenance: provTap("move_item") });
      if (!data.ok) { scanRefuse(data.message || "Undo failed."); toast.error(data.message || "Undo failed."); return; }
      scanAccept(data.message);
      toast.success(data.message || "Move undone.");
      setLog((l) => l.map((x) => (x === entry ? { ...x, undone: true } : x)));
      setLast((le) => (le === entry ? { ...le, undone: true } : le));
    } catch (e) { scanRefuse(formatApiErrorDetail(e.response?.data?.detail) || "Undo failed."); toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Undo failed."); }
  };
  const latestOk = log.find((x) => x.ok && !x.already && x.itemId);   // newest real move = what undo targets

  return (
    <div data-testid="move-item-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{inFloor ? floorTitle : "Move Item"}</h1>
          <p className="text-sm text-slate-500">Set a destination once, then scan any item — part, tote, or truck — into it. Each move is confirmed on screen with a beep; no taps, no blocking pop-ups.</p>
        </div>
        <SoundToggle />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
        <div className="space-y-4">
          {/* DESTINATION — sticky. Scan (future-proof) primary; dropdown (quick today) + type (fallback). */}
          <div className="border border-slate-200 bg-white rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#B01E28]" />Destination</Label>
              <button
                type="button"
                data-testid="zones-only-toggle"
                aria-pressed={zonesOnly}
                onClick={toggleZonesOnly}
                className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full px-3 py-1.5 border transition-colors ${zonesOnly ? "bg-[#B01E28] border-[#B01E28] text-white" : "bg-white border-slate-200 text-slate-500 hover:border-[#B01E28] hover:text-[#B01E28]"}`}>
                <Layers className="w-3.5 h-3.5" />Zones only{zonesOnly ? " · ON" : ""}
              </button>
            </div>
            {zonesOnly && <p data-testid="zones-only-hint" className="text-[11px] text-slate-500 mb-2">Fast yard mode — destination limited to yard zones. Scan/pick/type a zone; finer locations are rejected.</p>}
            <ScanField testid="dest-scan" submitLabel="Set" placeholder={zonesOnly ? "Scan a yard zone, or pick / type below" : "Scan a location, or pick / type below"}
              onScan={setDestByScan} autoFocus={false} />
            {dest ? (
              <div className="mt-2 flex items-center gap-2">
                <div data-testid="active-dest" className="text-sm font-mono bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 flex-1">ACTIVE → {dest.label}</div>
                <Button data-testid="change-dest" variant="outline" size="sm" className="rounded-lg border-slate-200" onClick={() => setDest(null)}>Change</Button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2 mt-2" data-testid="dest-fallback">
                <Select value="" onValueChange={pickDest}>
                  <SelectTrigger data-testid="dest-select" className="rounded-lg border-slate-200"><SelectValue placeholder={zonesOnly ? "Pick a yard zone…" : "Pick a location…"} /></SelectTrigger>
                  <SelectContent className="rounded-lg max-h-72">{destOptions.map((l) => <SelectItem key={l.id} value={l.id}>{l.short_code} · {fullLabel(l)}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="dest-type-input" placeholder={zonesOnly ? "…or type zone code" : "…or type code"} onKeyDown={(e) => { if (e.key === "Enter") typeDest(e.currentTarget.value); }}
                  onBlur={(e) => typeDest(e.target.value)} className="rounded-lg border-slate-200 sm:max-w-[180px] font-mono" />
              </div>
            )}
          </div>

          {/* ITEM — scan any item (auto-detects part / tote / truck) */}
          <div className="border border-slate-200 bg-white rounded-xl p-4">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><ScanLine className="w-3.5 h-3.5 text-[#B01E28]" />Scan item to move</Label>
            {dest ? (
              <ScanField testid="item-scan" submitLabel="Move" continuous autoFocus
                placeholder="Scan a part, tote, or truck…" onScan={moveItem} />
            ) : (
              <p className="text-sm text-slate-400">Set the destination first, then rapid-fire scan items into it.</p>
            )}
          </div>

          {/* BIG FLASH */}
          {last && (() => {
            const tone = !last.ok ? "border-red-500 bg-red-50" : (last.warn || last.already) ? "border-amber-500 bg-amber-50" : "border-emerald-500 bg-emerald-50";
            const Icon = !last.ok ? XCircle : (last.warn || last.already) ? AlertTriangle : CheckCircle2;
            const color = !last.ok ? "text-red-600" : (last.warn || last.already) ? "text-amber-600" : "text-emerald-600";
            return (
              <div data-testid="move-flash" className={`border-2 rounded-xl p-6 text-center ${tone}`}>
                <Icon className={`w-12 h-12 mx-auto ${color}`} />
                {last.ok ? (
                  <div className="mt-2">
                    <div className="font-display text-2xl font-black">{last.label}</div>
                    <div className="text-sm text-slate-700">{last.sub}</div>
                    <div className="font-mono text-xs text-slate-600 mt-1">{last.line}</div>
                    {!last.already && !last.warn && <div className="font-mono text-[11px] text-emerald-700 mt-1">✓ got it — pull tag away, scan next</div>}
                    {last.undone ? (
                      <div data-testid="move-flash-undone" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-500"><Undo2 className="w-3.5 h-3.5" />Undone — restored to prior location</div>
                    ) : last.itemId ? (
                      <Button data-testid="undo-last-move" variant="outline" size="sm" onClick={() => undoMove(last)}
                        className="mt-3 rounded-lg border-slate-300 hover:bg-white"><Undo2 className="w-4 h-4 mr-1.5" />Undo this move</Button>
                    ) : null}
                  </div>
                ) : (
                  <div className="mt-2 font-mono text-sm text-red-700">{last.message}</div>
                )}
              </div>
            );
          })()}

          {log.length > 0 && (
            <Button data-testid="move-finish" onClick={finishBatch} variant="outline"
              className="w-full rounded-lg border-slate-300 h-11">
              <CheckCircle2 className="w-4 h-4 mr-1.5" />Done — finish batch ({okCount} moved)
            </Button>
          )}
        </div>

        {/* SESSION LOG */}
        <div className="border border-slate-200 bg-white rounded-xl overflow-hidden">
          <div className="bg-slate-100 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-slate-500 flex justify-between">
            <span>Session log</span>
            <span><span className="text-emerald-600" data-testid="ok-count">{okCount} ok</span> · <span className="text-amber-600" data-testid="dup-count">{dupCount} dup</span> · <span className="text-red-600" data-testid="err-count">{errCount} err</span></span>
          </div>
          <div className="max-h-[60dvh] overflow-y-auto">
            {log.length === 0 ? <div className="p-6 text-center font-mono text-xs text-slate-400">NO MOVES YET</div>
              : log.map((x, i) => {
                const Icon = !x.ok ? XCircle : (x.warn || x.already) ? AlertTriangle : CheckCircle2;
                const M = KIND_META[x.kind];
                return (
                  <div key={i} className={`flex items-center gap-2 px-3 py-2 border-t border-slate-100 text-xs ${!x.ok ? "bg-red-50" : (x.warn || x.already) ? "bg-amber-50" : ""}`}>
                    <Icon className={`w-4 h-4 shrink-0 ${!x.ok ? "text-red-600" : (x.warn || x.already) ? "text-amber-600" : "text-emerald-600"}`} />
                    {x.ok ? (
                      <span className={`font-mono flex items-center gap-1 flex-1 ${x.undone ? "line-through text-slate-400" : ""}`}>
                        {M?.icon ? <M.icon className="w-3 h-3 text-slate-400" /> : null}{x.line}
                      </span>
                    ) : (
                      <span className="font-mono text-red-700 truncate flex-1">{x.code}: {x.message}</span>
                    )}
                    {x.ok && x.undone && <span className="text-[10px] font-mono uppercase text-slate-400">undone</span>}
                    {x.ok && !x.undone && x === latestOk && x.itemId && (
                      <button data-testid="undo-log-row" onClick={() => undoMove(x)} title="Undo this move"
                        className="text-slate-400 hover:text-[#B01E28] transition-colors"><Undo2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
